import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import '@/styles/workspace.css';
import '@/styles/google-ads.css';

const GPT_PLUGIN_URL = 'https://chatgpt.com/g/g-69a8218ff63081918427691c91ab57eb-zima-media';

interface Customer {
  id: number;
  customer_id: string;
  name: string;
  is_default: boolean;
  linked_at: string;
}

interface AccessibleAccount {
  customer_id: string;
  name: string;
  already_linked: boolean;
  is_mcc?: boolean;
}

interface ConnectionStatus {
  connected: boolean;
  customers: Customer[];
  default_customer_id: string | null;
  mcp_enabled: boolean;
}

export default function WorkspacePage() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [settingDefault, setSettingDefault] = useState<string | null>(null);
  const [unlinking, setUnlinking] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [accounts, setAccounts] = useState<AccessibleAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [linking, setLinking] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkSuccess, setLinkSuccess] = useState<string | null>(null);

  const [mccMode, setMccMode] = useState(false);
  const [selectedMcc, setSelectedMcc] = useState<AccessibleAccount | null>(null);
  const [mccSubaccounts, setMccSubaccounts] = useState<AccessibleAccount[]>([]);
  const [loadingSubaccounts, setLoadingSubaccounts] = useState(false);
  const [mccSearch, setMccSearch] = useState('');
  const [copiedUrl, setCopiedUrl] = useState(false);

  const errorMessages: Record<string, string> = {
    auth_failed: 'Failed to connect to Google Ads. Please try again.',
    no_token: 'Could not obtain an access token. Please try again.',
    discovery_failed: 'Failed to discover your Google Ads accounts. Please try again.',
  };

  const urlError = params.get('error');

  const hasAccounts = (status?.customers?.length ?? 0) > 0;
  const step2Unlocked = hasAccounts;
  const hasPaidPlan = user?.plan === 'pro' || user?.plan === 'agent' ||
    user?.subscription_status === 'active' || user?.subscription_status === 'trialing';
  const isAgencyPlan = user?.plan === 'agent';

  const fetchStatus = async () => {
    try {
      const res = await fetch('/api/google-ads/status');
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = async () => {
    setShowModal(true);
    setLinkError(null);
    setLinkSuccess(null);
    setMccMode(false);
    setSelectedMcc(null);
    setMccSubaccounts([]);
    setMccSearch('');
    setLoadingAccounts(true);
    try {
      const res = await fetch('/api/google-ads/accessible-accounts');
      if (res.ok) {
        const data = await res.json();
        const accs: AccessibleAccount[] = data.accounts || [];
        const hasMcc = accs.some((a: AccessibleAccount) => a.is_mcc);
        if (hasMcc && isAgencyPlan) {
          setMccMode(true);
        }
        setAccounts(accs);
      } else {
        setShowModal(false);
        const mode = status?.connected ? 'add' : 'link';
        window.location.href = `/api/google-ads/connect?mode=${mode}`;
        return;
      }
    } catch {
      setShowModal(false);
      const mode = status?.connected ? 'add' : 'link';
      window.location.href = `/api/google-ads/connect?mode=${mode}`;
      return;
    } finally {
      setLoadingAccounts(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    const discovered = params.get('discovered');
    if (discovered === 'true') {
      setParams({}, { replace: true });
      openAddModal();
    }
  }, []);

  const handleLink = async (customerId: string) => {
    setLinking(customerId);
    setLinkError(null);
    setLinkSuccess(null);

    let endpoint: string;
    if (selectedMcc) {
      endpoint = '/api/google-ads/add-from-mcc';
    } else if (status?.connected) {
      endpoint = '/api/google-ads/add';
    } else {
      endpoint = '/api/google-ads/link';
    }

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId }),
      });
      const data = await res.json();
      if (res.ok) {
        setLinkSuccess(`Successfully added account ${data.name || customerId}`);
        if (selectedMcc) {
          setMccSubaccounts((prev) =>
            prev.map((a) =>
              a.customer_id === customerId ? { ...a, already_linked: true } : a
            )
          );
        } else {
          setAccounts((prev) =>
            prev.map((a) =>
              a.customer_id === customerId ? { ...a, already_linked: true } : a
            )
          );
        }
        await fetchStatus();
        await refresh();
      } else {
        setLinkError(data.error || 'Failed to add account');
      }
    } catch {
      setLinkError('Failed to add account');
    } finally {
      setLinking(null);
    }
  };

  const handleSelectMcc = async (mcc: AccessibleAccount) => {
    setSelectedMcc(mcc);
    setLoadingSubaccounts(true);
    setLinkError(null);
    setLinkSuccess(null);
    setMccSearch('');
    try {
      const res = await fetch('/api/google-ads/mcc-subaccounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mcc_id: mcc.customer_id }),
      });
      const data = await res.json();
      if (res.ok) {
        setMccSubaccounts(data.accounts);
      } else {
        setLinkError(data.error || 'Failed to load MCC sub-accounts');
      }
    } catch {
      setLinkError('Failed to load MCC sub-accounts');
    } finally {
      setLoadingSubaccounts(false);
    }
  };

  const filteredSubaccounts = useMemo(() => {
    if (!mccSearch.trim()) return mccSubaccounts;
    const q = mccSearch.toLowerCase();
    return mccSubaccounts.filter(
      (a) => a.name.toLowerCase().includes(q) || a.customer_id.includes(q)
    );
  }, [mccSubaccounts, mccSearch]);

  const displayedSubaccounts = filteredSubaccounts.slice(0, 50);

  const handleUnlink = async (customerId: string) => {
    if (!confirm(`Are you sure you want to unlink account ${customerId}?`)) return;
    setUnlinking(customerId);
    try {
      const res = await fetch('/api/google-ads/unlink', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId }),
      });
      if (res.ok) {
        await fetchStatus();
        await refresh();
      }
    } catch {
    } finally {
      setUnlinking(null);
    }
  };

  const handleSetDefault = async (customerId: string) => {
    setSettingDefault(customerId);
    try {
      const res = await fetch('/api/google-ads/customers/default', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId }),
      });
      if (res.ok) {
        const data = await res.json();
        setStatus((prev) => prev ? {
          ...prev,
          default_customer_id: data.default_customer_id,
          mcp_enabled: data.mcp_enabled,
          customers: prev.customers.map((c) => ({
            ...c,
            is_default: c.customer_id === data.default_customer_id,
          })),
        } : prev);
        await refresh();
      }
    } catch {
    } finally {
      setSettingDefault(null);
    }
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="mcc-result-highlight">{text.slice(idx, idx + query.length)}</mark>
        {text.slice(idx + query.length)}
      </>
    );
  };

  if (loading) {
    return <div className="gads-loading">Loading...</div>;
  }

  const mccAccounts = accounts.filter((a) => a.is_mcc);
  const personalAccounts = accounts.filter((a) => !a.is_mcc);

  const getModalTitle = () => {
    if (mccMode && selectedMcc) {
      return `Accounts in ${selectedMcc.name || 'MCC'}`;
    }
    if (mccMode && !selectedMcc) {
      return 'Select an MCC';
    }
    return 'Select Accounts to Add';
  };

  const renderAccountList = (accountList: AccessibleAccount[]) => (
    <div className="gads-customers-list">
      {accountList.map((account) => (
        <div key={account.customer_id} className="gads-customer-row">
          <div className="gads-customer-info">
            <span className="gads-customer-name">
              {selectedMcc ? highlightMatch(account.name, mccSearch) : account.name}
            </span>
            <span className="gads-customer-id">
              {selectedMcc ? highlightMatch(account.customer_id, mccSearch) : account.customer_id}
            </span>
          </div>
          <div className="gads-customer-actions">
            {account.already_linked ? (
              <span className="gads-badge gads-badge-active">Added</span>
            ) : (
              <button
                onClick={() => handleLink(account.customer_id)}
                disabled={linking === account.customer_id}
                className="btn btn-primary gads-link-btn"
              >
                {linking === account.customer_id ? 'Adding...' : 'Add Account'}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="workspace-builder">
      {!hasPaidPlan && (
        <div className="ws-payment-banner ws-payment-banner-info">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div className="ws-payment-banner-content">
            <strong>Starter Plan</strong>
            <span>Upgrade to Pro or Agency for unlimited chats, more accounts, and advanced features. </span>
            <Link to="/pricing" className="ws-payment-banner-link">View plans &rarr;</Link>
          </div>
        </div>
      )}

      {urlError && errorMessages[urlError] && (
        <div className="gads-error" style={{ marginBottom: 20 }}>{errorMessages[urlError]}</div>
      )}

      <div className="ws-step-row">
        <div className="ws-step-label">
          <span className="ws-step-number">Step 1</span>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </div>

        <div className="ws-step-card">
          <div className="ws-step-card-header">
            <h2 className="ws-step-title">Workspace</h2>
            {status?.mcp_enabled ? (
              <span className="gads-badge gads-badge-active">MCP Active</span>
            ) : (
              <span className="gads-badge gads-badge-inactive">MCP Inactive</span>
            )}
          </div>

          {hasAccounts && (
            <div className="gads-customers-list" style={{ marginBottom: 16 }}>
              {status!.customers.map((customer) => (
                <div
                  key={customer.customer_id}
                  className={`gads-customer-row${customer.customer_id === status!.default_customer_id ? ' active' : ''}`}
                >
                  <div className="gads-customer-info">
                    <span className="gads-customer-name">{customer.name}</span>
                    <span className="gads-customer-id">{customer.customer_id}</span>
                  </div>
                  <div className="gads-customer-actions">
                    {customer.customer_id === status!.default_customer_id ? (
                      <span className="gads-badge gads-badge-default">Default</span>
                    ) : (
                      <button
                        onClick={() => handleSetDefault(customer.customer_id)}
                        disabled={settingDefault === customer.customer_id}
                        className="btn btn-ghost gads-set-default-btn"
                      >
                        {settingDefault === customer.customer_id ? 'Setting...' : 'Set Default'}
                      </button>
                    )}
                    <button
                      onClick={() => handleUnlink(customer.customer_id)}
                      disabled={unlinking === customer.customer_id}
                      className="btn btn-ghost gads-unlink-btn"
                    >
                      {unlinking === customer.customer_id ? 'Removing...' : 'Unlink'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="ws-add-row">
            {status?.connected ? (
              <button onClick={openAddModal} className="ws-add-btn">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add
              </button>
            ) : (
              <button
                className="ws-add-btn"
                onClick={async () => {
                  try {
                    const resp = await fetch('/api/google-ads/connect', { redirect: 'manual' });
                    if (resp.type === 'opaqueredirect' || (resp.status >= 300 && resp.status < 400)) {
                      window.location.href = resp.headers.get('Location') || '/api/google-ads/connect';
                    } else if (resp.ok) {
                      window.location.href = '/api/google-ads/connect';
                    } else {
                      const data = await resp.json().catch(() => ({}));
                      setLinkError(data.error || 'Failed to connect Google Ads. Please try again.');
                    }
                  } catch {
                    window.location.href = '/api/google-ads/connect';
                  }
                }}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add
              </button>
            )}
          </div>
          {linkError && <div className="gads-error" style={{ marginTop: 10 }}>{linkError}</div>}
        </div>
      </div>

      <div className={`ws-divider${step2Unlocked ? ' unlocked' : ''}`}>
        <span className="ws-divider-text">Proceed to Next Step</span>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
          {step2Unlocked ? (
            <><circle cx="12" cy="12" r="10" /><polyline points="9 12 12 15 15 9" /></>
          ) : (
            <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>
          )}
        </svg>
      </div>

      <div className={`ws-step-row${!step2Unlocked ? ' ws-step-locked' : ''}`}>
        <div className="ws-step-label">
          <span className="ws-step-number">Step 2</span>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </div>

        <div className="ws-step-card">
          <div className="ws-step2-content">
            <div className="ws-chatgpt-icon">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
              </svg>
            </div>
            <div className="ws-step2-text">
              {user?.chatgpt_authenticated ? (
                <>
                  <span>ChatGPT </span>
                  <strong>Connected</strong>
                </>
              ) : (
                <>
                  <span>Complete Authentication with </span>
                  <strong>ChatGPT</strong>
                </>
              )}
            </div>
            <div className="ws-step2-status">
              {user?.chatgpt_authenticated ? (
                <>
                  {user?.chatgpt_active ? (
                    <span className="gads-badge gads-badge-active">Active</span>
                  ) : (
                    <span className="gads-badge gads-badge-neutral">Connected</span>
                  )}
                  <a
                    href={GPT_PLUGIN_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary ws-auth-btn"
                    style={{ marginLeft: 8 }}
                  >
                    Start Chat
                  </a>
                </>
              ) : (
                <>
                  <span className="gads-badge gads-badge-inactive">Not Connected</span>
                  {step2Unlocked && (
                    <a
                      href={GPT_PLUGIN_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary ws-auth-btn"
                      style={{ marginLeft: 8 }}
                    >
                      Connect ChatGPT
                    </a>
                  )}
                </>
              )}
            </div>
            <div className="ws-copy-url">
              <button
                className="btn btn-ghost ws-copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(GPT_PLUGIN_URL).then(() => {
                    setCopiedUrl(true);
                    setTimeout(() => setCopiedUrl(false), 2000);
                  });
                }}
                title="Copy plugin URL"
              >
                {copiedUrl ? (
                  <>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    <span>Copy URL</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="gads-modal-overlay" onClick={() => setShowModal(false)}>
          <div className={`gads-modal${selectedMcc ? ' gads-modal-wide' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="gads-modal-header">
              <div className="gads-modal-header-left">
                {selectedMcc && (
                  <button
                    onClick={() => { setSelectedMcc(null); setMccSubaccounts([]); setMccSearch(''); setLinkError(null); setLinkSuccess(null); }}
                    className="gads-modal-back"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <h2>{getModalTitle()}</h2>
              </div>
              <button onClick={() => setShowModal(false)} className="gads-modal-close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="gads-modal-body">
              {linkError && <div className="gads-error">{linkError}</div>}
              {linkSuccess && <div className="gads-success">{linkSuccess}</div>}

              {loadingAccounts ? (
                <div className="gads-loading">Loading your accounts...</div>
              ) : mccMode && !selectedMcc ? (
                mccAccounts.length === 0 ? (
                  <div className="gads-empty">No MCC accounts found. Try adding a personal account instead.</div>
                ) : (
                  <>
                    <p className="mcc-step-hint">Select the MCC account to browse its sub-accounts.</p>
                    <div className="gads-customers-list">
                      {mccAccounts.map((account) => (
                        <div key={account.customer_id} className="gads-customer-row mcc-selectable" onClick={() => handleSelectMcc(account)}>
                          <div className="gads-customer-info">
                            <span className="gads-customer-name">{account.name}</span>
                            <span className="gads-customer-id">{account.customer_id}</span>
                          </div>
                          <div className="gads-customer-actions">
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M9 18l6-6-6-6" />
                            </svg>
                          </div>
                        </div>
                      ))}
                    </div>
                    {personalAccounts.length > 0 && (
                      <>
                        <p className="mcc-step-hint" style={{ marginTop: 16 }}>Or add a personal account directly:</p>
                        {renderAccountList(personalAccounts)}
                      </>
                    )}
                  </>
                )
              ) : selectedMcc ? (
                loadingSubaccounts ? (
                  <div className="gads-loading">Loading sub-accounts...</div>
                ) : (
                  <>
                    <div className="mcc-search-wrap">
                      <svg className="mcc-search-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <input
                        type="text"
                        className="input mcc-search-input"
                        placeholder="Search by account name or ID..."
                        value={mccSearch}
                        onChange={(e) => setMccSearch(e.target.value)}
                        autoFocus
                      />
                      {mccSearch && (
                        <button className="mcc-search-clear" onClick={() => setMccSearch('')}>
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <div className="mcc-results-count">
                      {mccSearch.trim()
                        ? `Showing ${displayedSubaccounts.length} of ${filteredSubaccounts.length} matches (${mccSubaccounts.length} total)`
                        : `${mccSubaccounts.length} accounts`}
                    </div>
                    {filteredSubaccounts.length === 0 ? (
                      <div className="gads-empty">No accounts match your search.</div>
                    ) : (
                      <div className="mcc-results-list">
                        {renderAccountList(displayedSubaccounts)}
                      </div>
                    )}
                  </>
                )
              ) : accounts.length === 0 ? (
                <div className="gads-empty">No accessible accounts found for your Google account.</div>
              ) : (
                renderAccountList(accounts)
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
